import {
  Component,
  computed,
  inject,
  input,
  resource,
} from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { coerceNumber } from '../../cdk/utils';
import { MaterialIconRegistry } from './material-icon-registry.service';
import { ICON_MODE } from './providers';
import type { IconName } from './type';

const ICON_SIZE_MAPPING = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 40,
  xxl: 64,
  xxxl: 96,
  ys: 128,
  ym: 160,
  yl: 192,
  yx: 224,
  ye: 256,
};

export type IconSize = keyof typeof ICON_SIZE_MAPPING | number;

@Component({
  selector: 'tvd-icon',
  template: `
    <div class="flex" [style.width]="iconSize()" [style.height]="iconSize()">
      @if (icon() && iconMode() === 'font') {
        <span
          class="material-symbols-outlined flex justify-center items-center aspect-square"
          [style.width]="iconSize()"
          [style.height]="iconSize()"
          [style.font-size]="iconSize()"
          [attr.data-icon-name]="icon()"
          >{{ icon() }}</span
        >
      } @else {
        <span
          [attr.data-icon-name]="icon()"
          [innerHTML]="sanitizedSvg()"
          class="flex justify-center items-center aspect-square"
          [style.width]="iconSize()"
          [style.height]="iconSize()"
        ></span>
      }
    </div>
  `,
  host: { class: 'inline-block w-fit' },
})
export class Icon {
  readonly #materialRegistry = inject(MaterialIconRegistry);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #iconMode = inject(ICON_MODE, { optional: true });

  readonly iconMode = computed(() => this.#iconMode ?? 'font');

  readonly size = input<number | undefined, IconSize | number | undefined>(
    undefined,
    {
      transform: (size) => {
        if (size === undefined || size === null) {
          return undefined;
        }

        return typeof size === 'string' && ICON_SIZE_MAPPING[size]
          ? ICON_SIZE_MAPPING[size]
          : coerceNumber(size);
      },
    },
  );

  readonly icon = input<IconName | string | null>();

  /** Only used for illustrations*/
  readonly light = input(false);

  readonly iconSize = computed(() =>
    this.size() !== undefined ? `${this.size()}px` : '1em',
  );

  readonly s = resource({
    params: computed(() => ({
      icon: this.icon(),
      mode: this.iconMode(),
    })),
    loader: async ({ params }) => {
      if (!params.icon) {
        return '';
      }

      if (params.mode === 'svg') {
        const icon = await this.#materialRegistry.getIcon(params.icon);
        if (icon) {
          return this.#withSize(icon);
        }
      }

      return '';
    },
    defaultValue: '',
  });

  readonly sanitizedSvg = computed(() => {
    if (this.icon() && this.iconMode() === 'font') {
      return '';
    }

    if (this.s.isLoading()) {
      return '';
    }

    return this.#sanitizer.bypassSecurityTrustHtml(this.s.value() ?? '');
  });

  #withSize(svg: string): string {
    const size = this.size();
    const width = size !== undefined ? `${size}` : '100%';
    const height = size !== undefined ? `${size}` : '100%';

    return svg.replace(/<svg\b([^>]*)>/, (_match, attrs) => {
      const cleanedAttrs = String(attrs)
        .replace(/\swidth="[^"]*"/g, '')
        .replace(/\sheight="[^"]*"/g, '')
        .replace(/\sfill="[^"]*"/g, '');

      return `<svg width="${width}" height="${height}" fill="currentColor"${cleanedAttrs}>`;
    });
  }
}
